<script lang="ts">
  // A dashboard row's review detail, the same for stacked, single and flat
  // rows: reviewers with their verdicts, unresolved threads, checks, and the
  // viewer's progress where they opened the pull request at its head.
  import { progressWords, type PrCounts } from '../logic/prSummary';
  import type { RowReviewer, ReviewSummary } from '../logic/reviewSummary';
  import { relTime } from '../logic/time';
  import { checkState, type CheckState } from '../logic/stackState';

  interface Props {
    /** Null while unknown (the attention inputs have not loaded, or failed). */
    review: ReviewSummary | null;
    /** What the row's review state says beside the avatars (`rowReview`'s notes). */
    notes: string[];
    /** GitHub's `statusCheckRollup.state`. */
    checks: string | null | undefined;
    /** Recorded on this device at the pull request's current head (`at`, epoch ms); null when none was. */
    summary: (PrCounts & { at: number }) | null;
  }
  let { review, notes, checks, summary }: Props = $props();

  const CI_SHORT: Record<CheckState, string> = { passing: 'CI passing', failing: 'CI failing', pending: 'CI running', none: '' };
  const VERDICT_TEXT = { approved: 'approved', changes: 'requested changes', commented: 'commented' } as const;
  /** A reviewer in words: their verdict, and whether a review is asked of them (again). */
  const said = (x: RowReviewer) =>
    x.verdict ? `${VERDICT_TEXT[x.verdict]}${x.requested ? ', asked again' : ''}` : 'asked to review';

  const ci = $derived(checkState(checks));
  const shown = $derived(review ? review.reviewers.slice(0, review.reviewers.length > 4 ? 3 : 4) : []);
  const words = $derived(summary ? progressWords(summary) : null);
  const progressText = $derived(words && summary ? `${words.full}, on this device as of ${relTime(new Date(summary.at).toISOString())}` : '');
</script>

<div class="stack-detail">
  {#if review?.reviewers.length}
    <span class="stack-reviewers"
      ><span class="sr-only">Reviews: {review.reviewers.map((x) => `${x.login} ${said(x)}`).join('; ')}.</span
      ><span class="stack-faces" aria-hidden="true"
        >{#each shown as x (x.login)}<span class={['stack-face', x.verdict, x.requested && 'requested'].filter(Boolean).join(' ')} title="{x.login}: {said(x)}"
            >{#if x.avatarUrl}<img src={x.avatarUrl} alt="" loading="lazy" />{:else}<span class="stack-initial">{x.login.slice(0, 1).toUpperCase()}</span
              >{/if}{#if x.verdict === 'approved'}<i class="stack-verdict">✓</i>{:else if x.verdict === 'changes'}<i class="stack-verdict">✕</i>{/if}</span
          >{/each}{#if review.reviewers.length > shown.length}<span
            class="stack-more"
            title={review.reviewers
              .slice(shown.length)
              .map((x) => `${x.login}: ${said(x)}`)
              .join('\n')}>+{review.reviewers.length - shown.length}</span
          >{/if}</span
      ></span
    >
  {/if}
  {#if notes.length}<span class="stack-notes">{notes.join(' · ')}</span>{/if}
  {#if review?.unresolved}
    <span class="stack-threads" title="{review.unresolved} unresolved review thread{review.unresolved === 1 ? '' : 's'}"
      ><svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z" /></svg>{review.unresolved}<span class="sr-only">{` unresolved thread${review.unresolved === 1 ? '' : 's'}`}</span
      ></span
    >
  {/if}
  {#if ci !== 'none'}<span class="stack-ci {ci}">{CI_SHORT[ci]}</span>{/if}
  {#if words}
    <span class="stack-progress" title={progressText}
      ><span class="stack-bar" aria-hidden="true"><i style:width="{Math.round(words.share * 100)}%"></i></span><span aria-hidden="true"
        >{words.lines} · {words.blocks}</span
      ><span class="sr-only">{progressText}</span></span
    >
  {/if}
</div>
